import { useTranslation } from 'react-i18next';

import { useSetActivePackMutation, type AvatarItem, type AvatarMesh, type AvatarAnimationPack } from '@/services/avatarApi';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

/** Libreria d'animazione → chiave della sua etichetta. Una libreria che non e' qui mostra il nome che manda il server. */
const LIBRARY_LABEL: Record<string, string> = {
  'tripo-utility': 'packLibrary.action',
  'meshy-dancing': 'packLibrary.dancing',
};

function readyPacks(mesh: AvatarMesh | undefined): AvatarAnimationPack[] {
  if (!mesh?.animationPacks) return [];
  return mesh.animationPacks.filter(p => p.status === 'ready');
}

interface Props {
  avatar: AvatarItem;
}

export function PackSwitcher({ avatar }: Props) {
  const haptics = useHaptics();
  const { t } = useTranslation('avatars');
  const [setActivePack, { isLoading }] = useSetActivePackMutation();
  const packs = readyPacks(avatar.mesh);
  if (packs.length < 2) return null;
  const activeId = avatar.mesh?.activePackId;

  const onPick = (packId: string) => {
    if (packId === activeId) return;
    haptics.impact('light');
    setActivePack({ avatarId: avatar._id, packId });
  };

  return (
    <div className="flex w-full rounded-full bg-bg-tertiary p-1">
      {packs.map(pack => {
        const labelKey = LIBRARY_LABEL[pack.library];
        const label = labelKey ? t(labelKey) : pack.library;
        const active = pack.packId === activeId;
        return (
          <button
            key={pack.packId}
            type="button"
            // La scelta attiva lo DICE: `aria-pressed` e' anche cio' che la tiene teal mentre il cambio e' in volo
            // (la regola globale del bottone spento in `globals.css` la esclude). Senza, spariva sul binario.
            aria-pressed={active}
            disabled={isLoading}
            onClick={() => onPick(pack.packId)}
            className={cn(
              'flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
              active ? 'bg-brand text-bg' : 'bg-transparent text-fg-muted',
              isLoading && 'opacity-50',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
